import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cx } from './cx';

interface FieldShell {
  label: string;
  /** Texto de apoio. Fica FORA do <label>: dentro, ele entra no nome acessível
   *  do campo e o leitor de tela anuncia a instrução inteira como se fosse o
   *  rótulo. Esse bug existia em 9 formulários do front anterior. */
  hint?: string;
  error?: string;
  required?: boolean;
}

function useFieldIds(error?: string, hint?: string) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return {
    id,
    hintId,
    errorId,
    describedBy: [errorId, hintId].filter(Boolean).join(' ') || undefined,
  };
}

function Shell({
  label, hint, error, required, id, hintId, errorId, children,
}: FieldShell & { id: string; hintId?: string; errorId?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {required && <span className="field__required" aria-hidden="true">*</span>}
        {required && <span className="sr-only">(obrigatório)</span>}
      </label>
      {children}
      {/* O erro vem antes da dica na ordem de leitura: quem errou precisa da
          correção primeiro, não da instrução que já leu. */}
      {error && <p className="field__error" id={errorId}>{error}</p>}
      {hint && <p className="field__hint" id={hintId}>{hint}</p>}
    </div>
  );
}

export type InputProps = FieldShell & Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'>;

export function Input({ label, hint, error, required, ...rest }: InputProps) {
  const { id, hintId, errorId, describedBy } = useFieldIds(error, hint);
  return (
    <Shell label={label} hint={hint} error={error} required={required} id={id} hintId={hintId} errorId={errorId}>
      <input
        {...rest}
        id={id}
        className="control"
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
    </Shell>
  );
}

export type TextareaProps = FieldShell & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'id'>;

export function Textarea({ label, hint, error, required, ...rest }: TextareaProps) {
  const { id, hintId, errorId, describedBy } = useFieldIds(error, hint);
  return (
    <Shell label={label} hint={hint} error={error} required={required} id={id} hintId={hintId} errorId={errorId}>
      <textarea
        {...rest}
        id={id}
        className="control"
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
    </Shell>
  );
}

export type SelectProps = FieldShell & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'id'> & {
  options: ReadonlyArray<{ value: string; label: string }>;
  /** Opção vazia inicial. Sem ela um select obrigatório já nasce "respondido"
   *  com o primeiro item, e o usuário envia um valor que nunca escolheu. */
  placeholder?: string;
};

export function Select({ label, hint, error, required, options, placeholder, ...rest }: SelectProps) {
  const { id, hintId, errorId, describedBy } = useFieldIds(error, hint);
  return (
    <Shell label={label} hint={hint} error={error} required={required} id={id} hintId={hintId} errorId={errorId}>
      {/* O invólucro existe só para posicionar a seta; o <select> continua
          sendo o elemento rotulado e associado ao <label>. */}
      <div className="select-wrap">
        <select
          {...rest}
          id={id}
          className="control"
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
    </Shell>
  );
}

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'type'> & {
  children: ReactNode;
};

/** O <label> envolve o controle: a área clicável passa a ser o texto inteiro,
 *  não o quadrado de 18px. */
export function Checkbox({ children, ...rest }: CheckboxProps) {
  return (
    <label className={cx('checkbox')}>
      <input {...rest} type="checkbox" />
      <span className="checkbox__text">{children}</span>
    </label>
  );
}
